import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const base = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  expectedVersion: z.number().int().nonnegative(),
}).strict()

export const catalogStatusV1Schema = z.enum(["active", "inactive"])
export const pricingUnitV1Schema = z.enum(["each", "hour", "fixed", "unit"])
const timestamp = z.string().datetime({ offset: false })

export const catalogServiceUpsertV1Schema = base.extend({
  serviceId: entityIdSchema,
  code: z.string().regex(/^[A-Z0-9][A-Z0-9._-]{0,31}$/),
  displayName: z.string().trim().min(1).max(120),
  description: z.string().trim().min(1).max(2_000).nullable(),
  status: catalogStatusV1Schema,
  defaultDurationMinutes: z.number().int().min(1).max(10_080),
  billingEligible: z.boolean(),
}).strict()

export const priceBookUpsertV1Schema = base.extend({
  priceBookId: entityIdSchema,
  displayName: z.string().trim().min(1).max(120),
  currency: z.string().regex(/^[A-Z]{3}$/),
  legalEntityId: legalEntityIdSchema.nullable(),
  branchId: branchIdSchema.nullable(),
  status: catalogStatusV1Schema,
}).strict().refine((value) => value.branchId === null || value.legalEntityId !== null, {
  message: "A Branch-scoped Price Book requires its Legal Entity.",
})

export const pricingVersionPublishV1Schema = base.extend({
  pricingVersionId: entityIdSchema,
  serviceId: entityIdSchema,
  priceBookId: entityIdSchema,
  legalEntityId: legalEntityIdSchema.nullable(),
  branchId: branchIdSchema.nullable(),
  effectiveFrom: timestamp,
  effectiveUntil: timestamp.nullable(),
  unitAmountMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  currency: z.string().regex(/^[A-Z]{3}$/),
  pricingUnit: pricingUnitV1Schema,
}).strict().superRefine((value, context) => {
  if (value.branchId !== null && value.legalEntityId === null) {
    context.addIssue({ code: "custom", message: "A Branch requires its Legal Entity." })
  }
  if (value.effectiveUntil !== null && value.effectiveUntil <= value.effectiveFrom) {
    context.addIssue({ code: "custom", message: "The effective window must be positive." })
  }
})

export const pricingSnapshotCreateV1Schema = base.extend({
  pricingSnapshotId: entityIdSchema,
  serviceId: entityIdSchema,
  priceBookId: entityIdSchema,
  legalEntityId: legalEntityIdSchema.nullable(),
  branchId: branchIdSchema.nullable(),
  pricedAt: timestamp,
  quantityMillis: z.number().int().min(1).max(1_000_000_000),
  targetType: z.enum([
    "estimate", "estimate_version", "service_agreement",
    "service_order", "change_order", "preview",
  ]),
  targetId: entityIdSchema,
}).strict().refine((value) => value.branchId === null || value.legalEntityId !== null, {
  message: "A Branch requires its Legal Entity.",
})

export const pricingSnapshotV1Schema = z.object({
  pricingSnapshotId: entityIdSchema,
  pricingVersionId: entityIdSchema,
  serviceId: entityIdSchema,
  serviceCode: z.string().min(1).max(32),
  serviceDisplayName: z.string().min(1).max(120),
  pricedAt: timestamp,
  quantityMillis: z.number().int().positive(),
  unitAmountMinor: z.number().int().nonnegative(),
  subtotalMinor: z.number().int().nonnegative(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  pricingUnit: pricingUnitV1Schema,
  inputFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
}).strict()

export type CatalogServiceUpsertV1 = z.infer<typeof catalogServiceUpsertV1Schema>
export type PriceBookUpsertV1 = z.infer<typeof priceBookUpsertV1Schema>
export type PricingVersionPublishV1 = z.infer<typeof pricingVersionPublishV1Schema>
export type PricingSnapshotCreateV1 = z.infer<typeof pricingSnapshotCreateV1Schema>
export type PricingSnapshotV1 = z.infer<typeof pricingSnapshotV1Schema>
